import { Field, useFormikContext } from "formik";
import { type FC } from "react";
import {
  AutocompleteField,
  CheckboxField,
  MultiAutocompleteField,
  MultiCheckboxField,
  SelectField,
  StringDateRangePickerField,
} from "src/components/UI/Form";
import { WorkPacketFilter, type WorkPacketOwner } from "src/types/work-packets";
import { WorkPacketType } from "../../WorkPacketType";
import { WorkPacketView } from "../../WorkPacketView";
import { useWorkPacketsContext } from "../../WorkPacketsContext";
import { useUserOptions } from "../../useUserOptions";
import {
  filterableWorkPacketActions,
  myPacketOwnerViewOptions,
  staticFilterOptions,
} from "../helpers/getStaticFilterOptions";
import { useAccrualSubtypes } from "./hooks/useAccrualSubtypes";
import { useAuditAccountOptions } from "./hooks/useAuditAccountOptions";
import { useVendorOptions } from "./hooks/userVendorOptions";
import { useAccountOptions } from "./hooks/useAccountOptions";
import { EMPTY_ARRAY } from "src/utils/empty-values";
import { FilterFieldGrid } from "../../FilterFieldGrid";
import type { Option } from "components/UI/Form/SelectField";

export const AccrualFiltersForm: FC = () => {
  const { setFieldValue } = useFormikContext();
  const { currentView, currentWorkPacketType } = useWorkPacketsContext();

  const userOptions = useUserOptions();
  const accountOptions = useAccountOptions();
  const vendorOptions = useVendorOptions();
  const auditAccountOptions = useAuditAccountOptions();
  const accrualSubtypes = useAccrualSubtypes();

  return (
    <FilterFieldGrid>
      <Field
        name={WorkPacketFilter.VendorName}
        component={MultiAutocompleteField}
        label="Vendor Name"
        options={vendorOptions}
        freeText={false}
        setFieldValue={setFieldValue}
      />

      <Field
        name={WorkPacketFilter.StoreName}
        component={MultiAutocompleteField}
        label="Store Name"
        options={accountOptions}
        freeText={false}
        setFieldValue={setFieldValue}
        disableCommaSeparation
      />

      <Field
        name={WorkPacketFilter.ManualFilingUser}
        component={AutocompleteField}
        label="Manual Filing User"
        options={auditAccountOptions}
        getOptionLabel={(option: any) => option.title}
      />

      {currentView === WorkPacketView.AllPackets && (
        <>
          <Field
            name={WorkPacketFilter.CurrentPacketOwner}
            component={AutocompleteField}
            label="Disputing Work Packet Owner"
            options={userOptions}
            getOptionLabel={(option: WorkPacketOwner) => option.title}
            placeholder="Disputing Work Packet Owner"
            setFieldValue={setFieldValue}
          />
          <Field
            name={WorkPacketFilter.BackupPacketOwner}
            component={AutocompleteField}
            label="Backup Packet Owner"
            options={userOptions}
            getOptionLabel={(option: WorkPacketOwner) => option.title}
            placeholder="Backup Packet Owner"
            setFieldValue={setFieldValue}
          />
          <Field
            name={WorkPacketFilter.MonitoringPacketOwner}
            component={AutocompleteField}
            label="Monitoring Packet Owner"
            options={userOptions}
            getOptionLabel={(option: WorkPacketOwner) => option.title}
            placeholder="Monitoring Packet Owner"
            setFieldValue={setFieldValue}
          />
        </>
      )}

      <Field name={WorkPacketFilter.ObarCreatedDate} component={StringDateRangePickerField} label="OBAR Created Date" />

      <Field
        name={WorkPacketFilter.RecoveryStreamSubtype}
        component={AutocompleteField}
        label="Recovery Stream Subtype"
        options={accrualSubtypes}
      />

      <Field
        name={WorkPacketFilter.AgreementId}
        component={MultiAutocompleteField}
        options={EMPTY_ARRAY}
        label="Agreement ID"
        setFieldValue={setFieldValue}
        disableSuggestions
        fullWidth
      />

      <Field name={WorkPacketFilter.InvoiceDate} component={StringDateRangePickerField} label="Invoice Date" />

      <Field
        name={WorkPacketFilter.InvoiceNumber}
        component={MultiAutocompleteField}
        options={EMPTY_ARRAY}
        label="Invoice Number"
        setFieldValue={setFieldValue}
        disableSuggestions
        fullWidth
      />

      <Field name={WorkPacketFilter.DisputeCreatedDate} component={StringDateRangePickerField} label="Dispute Date" />

      <Field
        name={WorkPacketFilter.OriginalDisputeId}
        component={MultiAutocompleteField}
        options={EMPTY_ARRAY}
        label="Original Dispute ID"
        setFieldValue={setFieldValue}
        disableSuggestions
        fullWidth
      />

      <Field
        name={WorkPacketFilter.ConsolidatedDisputeId}
        component={MultiAutocompleteField}
        options={EMPTY_ARRAY}
        label="Consolidated Dispute ID"
        setFieldValue={setFieldValue}
        disableSuggestions
        fullWidth
      />

      <Field
        name={WorkPacketFilter.CurrentAction}
        component={AutocompleteField}
        label="Current Action"
        options={filterableWorkPacketActions[WorkPacketType.ACCRUALS]}
        groupBy={(option: any) => option.category}
        getOptionLabel={(option: any) => option.title}
        placeholder="Current Action"
        setFieldValue={setFieldValue}
        workPacketType={currentWorkPacketType}
      />

      <Field
        name={WorkPacketFilter.DisputeStatus}
        component={AutocompleteField}
        label="Dispute Status"
        options={staticFilterOptions[WorkPacketFilter.DisputeStatus]}
        getOptionLabel={(option: any) => option.title}
        placeholder="Dispute Status"
        setFieldValue={setFieldValue}
      />

      <Field type="checkbox" name={WorkPacketFilter.WithNotes} component={CheckboxField} label="With Notes" />

      <Field
        type="checkbox"
        name={WorkPacketFilter.MonitoringDate}
        component={StringDateRangePickerField}
        label="Monitoring Date"
      />

      <Field
        name={WorkPacketFilter.PaymentStatus}
        component={MultiCheckboxField}
        label="Payment Status"
        options={staticFilterOptions[WorkPacketFilter.PaymentStatus]}
        setFieldValue={setFieldValue}
        disableSuggestions
      />

      <Field
        name={WorkPacketFilter.AccrualRedFlags}
        component={MultiCheckboxField}
        label="Red Flags"
        options={staticFilterOptions[WorkPacketFilter.AccrualRedFlags]}
        setFieldValue={setFieldValue}
        disableSuggestions
      />

      {currentView === WorkPacketView.MyPackets && (
        <Field
          name={WorkPacketFilter.MyPacketsView}
          component={SelectField}
          label="Show me packets where I am a..."
          options={myPacketOwnerViewOptions[WorkPacketType.ACCRUALS]}
          getOptionLabel={(option: Option) => option.value}
          setFieldValue={setFieldValue}
        />
      )}

      <Field name={WorkPacketFilter.ShowMigrated} component={CheckboxField} label="Include migrated" />
    </FilterFieldGrid>
  );
};
